import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography, Modal } from '@mui/material';
import { useSelector } from 'react-redux';
import { useOkta } from '../../hooks/oktaHook';
import { fetchAllMembers } from '../HomePage/service';
import TeamsTable from './TeamsTable';
import PageHeader from '../../components/PageHeader/PageHeader';
import { getSanitizedMemberList } from './ExcludeMembers';
import warning from '../../assests/images/warning.svg';
import { getFunctionsList } from './Profile/service';
import EditProfileTeamsTable from './EditProfileTeamsTable';
import { CORE_USER, TPO_ADMIN } from '../../utils/Constants';

function SetupTeam() {
  const { userDetails } = useOkta();
  const selectedBu = useSelector((state) => state?.cycle?.selectedBu);
  const [teamLeads, setTeamLeads] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [functionOptions, setFunctionOptions] = useState([]);
  const isExistingUser = useSelector((state) => state?.general?.isExistingUser);
  const [openModal, setOpenModal] = useState(false);
  const [userRowDeatails, setUserRowDeatails] = useState({});
  const [isTableLoadedWithData, setIsTableLoadedWithData] = useState(false);

  const getFunctionsData = async (bu) => {
    try {
      const data = await getFunctionsList(bu); // API call to fetch the functions list

      setFunctionOptions(data || []); // Set the function options
    } catch (error) {
      setFunctionOptions([]); // If API call fails, set an empty list
      console.error('Error getting functions list:', error);
    }
  };

  useEffect(() => {
    if (selectedBu) {
      getFunctionsData(selectedBu); // Fetch function options when BU is selected
    }
  }, [selectedBu]);

  useEffect(() => {
    if (selectedBu && isExistingUser) {
      getTableData(selectedBu, selectedBu);
    }
  }, [selectedBu, isExistingUser, openModal]);

  const getTableData = (bu, team) => {
    setIsTableLoading(true);

    fetchAllMembers(bu, team)
      .then((res) => {
        if (res) {
          const listOfLead = res?.listOfLead;
          const listOfMember = getSanitizedMemberList(res?.listOfMember);
          setTeamLeads(listOfLead || []);
          setTableData(listOfMember || []);
        }
      })
      .catch((err) => {
        console.error(err);
        setTeamLeads([]);
        setTableData([]);
      })
      .finally(() => {
        setIsTableLoading(false);
        setIsTableLoadedWithData(true);
      });
  };

  const excludedFunctions = ['Others', 'Sector Team'];
  // Check if any required functions are missing in memberFunction field
  const missingFunctions = functionOptions?.filter(
    (func) =>
      !excludedFunctions.includes(func) &&
      !tableData.some((user) => user.memberFunction?.includes(func))
  );

  let filtered = [...missingFunctions];
  const hasChannel = filtered.includes('Channel');
  const hasRegion = filtered.includes('Region');

  if (hasChannel && hasRegion) {
    // Replace both with a single "Channel or Region"
    filtered = filtered.filter(f => f !== 'Channel' && f !== 'Region');
    filtered.push('Region or Channel');
  } else {
    // Remove if only one exists
    filtered = filtered.filter(f => f !== 'Channel' && f !== 'Region');
  }
  const funcCount = filtered.length;
  const message = `Looks like you haven't assigned${funcCount > 1 ? ' ' + filtered.slice(0, -1).join(', ') + ' and ' + filtered[funcCount - 1] : ' a ' + filtered[0]} function${funcCount > 1 ? 's' : ''} for your Teams yet!`;
  const isWarningVisible =
    userDetails?.roles?.some((role) => [TPO_ADMIN, CORE_USER].includes(role)) && funcCount > 0 && isTableLoadedWithData;

  const handleOpenModal = (val) => {
    //
    setOpenModal(val);
  };

  const handleModalAction = (val, row) => {
    setOpenModal(val);
    setUserRowDeatails(row);
  };

  return (
    <Stack gap="24px" sx={{ padding: '24px', width: '100%' }}>
      {/* Show banner only if there are missing functions and the user is TPO_ADMIN */}
      {isWarningVisible && (
        <Box>
          <Stack
            direction="column"
            gap="8px"
            style={{ background: '#FFF2D2', padding: '0.5rem 0.75rem' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                fontSize: 'inherit',
                fontWeight: '700'
              }}>
              <img
                src={warning}
                alt="icon"
                style={{ position: 'relative', top: '6px', cursor: 'pointer' }}
              />
              <div style={{ padding: '0.5rem' }}>
                <Typography variant="h6">{message}</Typography>
                <Typography variant="body1" sx={{ color: '#616161', fontWeight: '400' }}>
                  Assign a role to them for your list of team members.
                </Typography>
              </div>
            </div>
          </Stack>
        </Box>
      )}

      <Box>
        <Stack direction="column" gap="8px">
          <PageHeader pageHeader={'Teams'} />
          <Typography variant="body1" sx={{ color: '#616161' }}>
            In this page you will configure your team by adding members and assigning specific roles
            based on their responsibilities.
          </Typography>
        </Stack>
      </Box>
      <TeamsTable
        tableData={tableData}
        teamLeads={teamLeads}
        i
        sTableLoading={isTableLoading}
        functionOptions={functionOptions}
        setTableData={setTableData}
        handleModalAction={handleModalAction}
      />
      {openModal ? (
        <Modal
          style={{
            margin: '0 auto',
            top: '10%',
            left: '10%'
          }}
          open={openModal}
          onClose={() => setOpenModal(false)}
          aria-labelledby="modal-title"
          aria-describedby="modal-description">
          <EditProfileTeamsTable
            tableData={tableData}
            userDetails={userDetails}
            handleOpenModal={handleOpenModal}
            userRowDeatails={userRowDeatails}
          />
        </Modal>
      ) : (
        <></>
      )}
    </Stack>
  );
}

export default SetupTeam;
